import { useCallback, useState } from "react";

export interface UseRovingTabIndexOptions {
  itemCount: number;
  /** Wrap from last→first and first→last (default false: clamp at the ends). */
  loop?: boolean;
  /** Which arrow pair navigates (default "vertical"). */
  orientation?: "vertical" | "horizontal";
  initialIndex?: number;
  /** Called when navigation moves the active index. */
  onActiveChange?: (index: number) => void;
}

export interface UseRovingTabIndexResult {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  /** Attach to the element that receives keyboard events (trigger or list).
   *  Returns true when the key was handled (caller can skip its own logic). */
  onKeyDown: (e: React.KeyboardEvent) => boolean;
}

/**
 * Arrow-key index management for composite widgets (strategy §14.3
 * `useRovingTabIndex`): Arrow prev/next, Home/End, loop or clamp.
 *
 * It owns WHICH item is active, not HOW activity is expressed — consumers
 * choose the APG flavor: move DOM focus between items (true roving tabindex:
 * menus, TabStrip) or keep focus on the trigger and point at the active item
 * with aria-activedescendant (listboxes: Select). Typeahead is a separate
 * primitive (lands with the menu/combobox increment).
 */
export function useRovingTabIndex({
  itemCount,
  loop = false,
  orientation = "vertical",
  initialIndex = 0,
  onActiveChange,
}: UseRovingTabIndexOptions): UseRovingTabIndexResult {
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  const move = useCallback(
    (index: number) => {
      setActiveIndex(index);
      onActiveChange?.(index);
    },
    [onActiveChange],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent): boolean => {
      if (itemCount === 0) return false;
      const [nextKey, prevKey] =
        orientation === "vertical"
          ? ["ArrowDown", "ArrowUp"]
          : ["ArrowRight", "ArrowLeft"];
      let next: number | undefined;
      if (e.key === nextKey) {
        next =
          activeIndex >= itemCount - 1
            ? loop
              ? 0
              : itemCount - 1
            : activeIndex + 1;
      } else if (e.key === prevKey) {
        next = activeIndex <= 0 ? (loop ? itemCount - 1 : 0) : activeIndex - 1;
      } else if (e.key === "Home") {
        next = 0;
      } else if (e.key === "End") {
        next = itemCount - 1;
      }
      if (next === undefined) return false;
      e.preventDefault();
      move(next);
      return true;
    },
    [activeIndex, itemCount, loop, orientation, move],
  );

  return { activeIndex, setActiveIndex: move, onKeyDown };
}
